import { Checkbox } from '@monorepo/shared/componentsV2/Checkbox';
import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { SelectField } from '@monorepo/shared/componentsV2/fields/SelectField';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import { isRequired } from '@monorepo/shared/utils/validators';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import styled from 'styled-components';
import { ModalFieldWrapper } from '../../styled';

type ChooseDefaultOptionFieldProps = {
  defaultValueOptions: SelectableOption<string>[];
  prefix?: string;
};

const NO_DEFAULT_VALUE = 'Unselected';

const ModalSelectField = styled(SelectField)`
  width: 50rem;
`;

const hasValidDefaultOption = (test: string | undefined | null) => {
  if (test?.length && test !== NO_DEFAULT_VALUE) return true;
  return false;
};

export const ChooseDefaultOptionField = (
  props: ChooseDefaultOptionFieldProps,
) => {
  const { defaultValueOptions, prefix } = props;

  const hasDefaultOptionFieldName = getFieldNameWithPrefix(
    'hasDefaultOption',
    prefix,
  );
  const defaultOptionFieldName = getFieldNameWithPrefix(
    'defaultOption',
    prefix,
  );
  const optionsFieldName = getFieldNameWithPrefix('options', prefix);

  const form = useForm();
  const {
    meta: { initial: initialValue },
  } = useField(defaultOptionFieldName, {
    subscription: { initial: true },
  });

  const {
    input: { value: optionsValue },
  } = useField(optionsFieldName, {
    subscription: { value: true },
  });

  const [hasDefaultOption, setHasDefaultOption] = useState(
    hasValidDefaultOption(initialValue),
  );

  const changeValue = useCallback(
    (value) => {
      const fieldState = form.getFieldState(defaultOptionFieldName);
      if (fieldState) fieldState.change(value);
    },
    [form, defaultOptionFieldName],
  );

  /*
    when we delete or change an option that is set as default,
    we need this explicit re-update - otherwise, we don't reset the defaultOption,
    and it retains the previously set value
  */
  useEffect(() => {
    if (!optionsValue.includes(initialValue)) {
      setHasDefaultOption(false);
      changeValue(NO_DEFAULT_VALUE);
    }
  }, [optionsValue, changeValue, form, initialValue]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isSet = event.currentTarget.checked;
      setHasDefaultOption(isSet);
      const currentValue = isSet ? initialValue : NO_DEFAULT_VALUE;
      changeValue(currentValue);
    },
    [initialValue, changeValue],
  );

  return (
    <>
      <ModalFieldWrapper>
        <Checkbox
          label="Assign default option"
          name={hasDefaultOptionFieldName}
          checked={hasDefaultOption}
          onChange={handleChange}
        />
      </ModalFieldWrapper>

      {hasDefaultOption && (
        <ModalFieldWrapper>
          <ModalSelectField
            label="Choose default option"
            name={defaultOptionFieldName}
            options={defaultValueOptions}
            validate={isRequired}
            required
            tooltipText="When users complete this form, this field will be filled out using this default when the user first opens the form."
          />
        </ModalFieldWrapper>
      )}
    </>
  );
};
